export const SCHEDULE_SERVICES_LISTING_TYPE = 'schedule-services';
export const SCHEDULE_SERVICE_MAX_DAYS = 30;
export const SCHEDULE_SERVICE_TIME_ZONE = 'America/Puerto_Rico';
export const SCHEDULE_SERVICE_EARLIEST_START_MINUTES = 5 * 60;
export const SCHEDULE_SERVICE_LATEST_START_MINUTES = 22 * 60;

export const isScheduleService = listingType => listingType === SCHEDULE_SERVICES_LISTING_TYPE;

export const isScheduleServiceStartTimeAllowed = startTime => {
  if (typeof startTime !== 'string') return false;
  const match = startTime.match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return false;

  const startMinutes = hours * 60 + minutes;
  return (
    startMinutes >= SCHEDULE_SERVICE_EARLIEST_START_MINUTES &&
    startMinutes < SCHEDULE_SERVICE_LATEST_START_MINUTES
  );
};

export const createClosedAvailabilityPlan = timezone => ({
  type: 'availability-plan/time',
  timezone,
  entries: [],
});

export const getScheduleServiceDuration = publicData => {
  const variants = publicData?.priceVariants || [];
  const durations = variants
    .map(variant => variant?.bookingLengthInMinutes)
    .filter(duration => Number.isFinite(duration) && duration > 0);
  return durations.length === 1 ? durations[0] : null;
};

export const sortFutureScheduleSlots = (exceptions = [], now = new Date()) =>
  exceptions
    .filter(exception => {
      const { start, end, seats } = exception?.attributes || {};
      return seats === 1 && start instanceof Date && end instanceof Date && end > now;
    })
    .sort((a, b) => a.attributes.start.getTime() - b.attributes.start.getTime());

export const validateScheduleSlot = ({ start, end, existingSlots = [], now = new Date() }) => {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return 'startRequired';
  if (!(end instanceof Date) || Number.isNaN(end.getTime()) || end <= start) {
    return 'durationInvalid';
  }
  if (start <= now) return 'past';

  const latestStart = new Date(now.getTime());
  latestStart.setDate(latestStart.getDate() + SCHEDULE_SERVICE_MAX_DAYS);
  if (start > latestStart) return 'tooFar';

  const conflict = existingSlots.find(slot => {
    const slotStart = slot?.attributes?.start;
    const slotEnd = slot?.attributes?.end;
    return (
      slotStart instanceof Date && slotEnd instanceof Date && start < slotEnd && end > slotStart
    );
  });
  if (!conflict) return null;

  const { start: conflictStart, end: conflictEnd } = conflict.attributes;
  return start.getTime() === conflictStart.getTime() && end.getTime() === conflictEnd.getTime()
    ? 'duplicate'
    : 'overlap';
};

export const createScheduleSlotParams = ({ listingId, start, durationInMinutes }) => ({
  listingId,
  start,
  end: new Date(start.getTime() + durationInMinutes * 60 * 1000),
  seats: 1,
});

export const isScheduleServiceSlotAvailable = (slot, timeSlots = []) => {
  const slotStart = slot?.attributes?.start;
  const slotEnd = slot?.attributes?.end;
  if (!(slotStart instanceof Date) || !(slotEnd instanceof Date)) return false;

  return timeSlots.some(timeSlot => {
    const { start, end, seats } = timeSlot?.attributes || {};
    return (
      seats > 0 &&
      start instanceof Date &&
      end instanceof Date &&
      start <= slotStart &&
      end >= slotEnd
    );
  });
};

export const canManageScheduleServiceSlot = ({ slot, timeSlots, isDraft, now = new Date() }) => {
  const start = slot?.attributes?.start;
  if (!(start instanceof Date) || start <= now) return false;
  return isDraft || isScheduleServiceSlotAvailable(slot, timeSlots);
};
